import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Logo from '../ui/Logo';

const navLinks = [
  { label: 'Home', path: '/' },
  { label: 'About', path: '/about' },
  {
    label: 'Services', path: '/services',
    sub: [
      { label: 'Residential', path: '/services/residential' },
      { label: 'Commercial', path: '/services/commercial' },
      { label: 'Industrial', path: '/services/industrial' },
    ]
  },
  { label: 'Products', path: '/remedies' },
  { label: 'Contact', path: '/contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMenuOpen(false); }, [location]);

  return (
    <motion.nav
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
        padding: scrolled ? '12px 0' : '20px 0',
        background: scrolled ? 'rgba(13,13,31,0.97)' : 'transparent',
        backdropFilter: scrolled ? 'blur(12px)' : 'none',
        borderBottom: scrolled ? '1px solid rgba(232,135,26,0.15)' : 'none',
        transition: 'all 0.4s ease',
      }}
    >
      <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Logo size={38} showText={true} animate={true} />

        {/* Desktop Nav */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 32 }} className="desktop-nav">
          {navLinks.map(link => (
            link.sub ? (
              <div key={link.label} style={{ position: 'relative' }}
                onMouseEnter={() => setServicesOpen(true)}
                onMouseLeave={() => setServicesOpen(false)}>
                <span style={{
                  fontFamily: 'Inter, sans-serif', fontSize: 14, cursor: 'pointer',
                  color: location.pathname.startsWith('/services') ? '#E8871A' : '#FDF6EC',
                  opacity: 0.85, letterSpacing: 0.3,
                  borderBottom: location.pathname.startsWith('/services') ? '1px solid #E8871A' : 'none',
                  paddingBottom: 2
                }}>
                  {link.label} ▾
                </span>
                <AnimatePresence>
                  {servicesOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 8 }}
                      style={{
                        position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)',
                        marginTop: 12, background: 'rgba(13,13,31,0.98)',
                        border: '1px solid rgba(232,135,26,0.2)', borderRadius: 6,
                        padding: '8px 0', minWidth: 160, backdropFilter: 'blur(12px)'
                      }}
                    >
                      {link.sub.map(s => (
                        <Link key={s.path} to={s.path} style={{
                          display: 'block', padding: '10px 20px',
                          fontFamily: 'Inter, sans-serif', fontSize: 13,
                          color: location.pathname === s.path ? '#E8871A' : '#FDF6EC',
                          textDecoration: 'none', opacity: 0.85,
                          transition: 'color 0.2s'
                        }}
                          onMouseEnter={e => (e.currentTarget.style.color = '#E8871A')}
                          onMouseLeave={e => (e.currentTarget.style.color = location.pathname === s.path ? '#E8871A' : '#FDF6EC')}
                        >
                          {s.label}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <Link key={link.path} to={link.path} style={{
                fontFamily: 'Inter, sans-serif', fontSize: 14,
                color: location.pathname === link.path ? '#E8871A' : '#FDF6EC',
                textDecoration: 'none', opacity: 0.85, letterSpacing: 0.3,
                borderBottom: location.pathname === link.path ? '1px solid #E8871A' : 'none',
                paddingBottom: 2, transition: 'color 0.2s'
              }}>
                {link.label}
              </Link>
            )
          ))}
          <Link to="/contact" className="btn-primary" style={{ padding: '10px 22px', fontSize: 13 }}>
            Book Consultation
          </Link>
        </div>

        {/* Hamburger */}
        <button onClick={() => setMenuOpen(!menuOpen)}
          style={{ display: 'none', background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
          className="hamburger" aria-label="Menu">
          <div style={{ width: 24, height: 2, background: '#E8871A', marginBottom: 5, transition: 'all 0.3s',
            transform: menuOpen ? 'rotate(45deg) translate(5px,5px)' : 'none' }} />
          <div style={{ width: 24, height: 2, background: '#E8871A', marginBottom: 5,
            opacity: menuOpen ? 0 : 1, transition: 'all 0.3s' }} />
          <div style={{ width: 24, height: 2, background: '#E8871A', transition: 'all 0.3s',
            transform: menuOpen ? 'rotate(-45deg) translate(5px,-5px)' : 'none' }} />
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            style={{ background: 'rgba(13,13,31,0.99)', borderTop: '1px solid rgba(232,135,26,0.15)', overflow: 'hidden' }}
          >
            <div style={{ padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: 4 }}>
              {navLinks.map(link => (
                <React.Fragment key={link.label}>
                  <Link to={link.path} style={{
                    padding: '12px 0', fontFamily: 'Inter, sans-serif', fontSize: 15,
                    color: location.pathname === link.path ? '#E8871A' : '#FDF6EC',
                    textDecoration: 'none', borderBottom: '1px solid rgba(232,135,26,0.08)'
                  }}>{link.label}</Link>
                  {link.sub?.map(s => (
                    <Link key={s.path} to={s.path} style={{
                      padding: '10px 0 10px 16px', fontFamily: 'Inter, sans-serif', fontSize: 13,
                      color: '#FDF6EC', textDecoration: 'none', opacity: 0.7,
                      borderBottom: '1px solid rgba(232,135,26,0.05)'
                    }}>— {s.label}</Link>
                  ))}
                </React.Fragment>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        @media (max-width: 900px) {
          .desktop-nav { display: none !important; }
          .hamburger { display: block !important; }
        }
      `}</style>
    </motion.nav>
  );
}
